import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { ReactComponent as ArrowLeftIcon } from 'assets/icons/arrow-left-icon.svg';
import { IconButton } from 'components/icon-button';
import { findMatchingRoute } from 'utils/findMatchingRoute';
import { RoutePath } from 'router/routes/routePath';
import { useValidatedRoutes } from 'router/hooks/useValidatedRoutes';
import { getStructureIdFromUrl } from 'utils/getStructureIdFromUrl';
import { useGetAncestors } from 'api/graphql/hooks/structures/useGetAncestors';
import { useGetStructure } from 'api/graphql/hooks/structures/useGetStructure';
import {
    generateStructureCrumb,
    singleStructureCrumb,
    useOrganizationDetailsCrumb,
    generateReferenceBreadcrumb,
} from 'components/breadcrumbs/helpers';
import {
    organizationsStart,
    structureStart,
    structuresList,
    usersList,
    organizationsList,
    structureAdd,
    settings,
    profile,
    system3e,
} from 'components/breadcrumbs/constants/staticCrumbs';
import { customScroll } from 'styles/globalStyle';
import { RouteArgs } from 'router/types';
import { useStructureManagement } from 'store/stucture/hooks/useStructureManagement';
import { useGetStructureChildren } from 'api/graphql/hooks/structures/useGetStructureChildren';
import { StructureType, Structure } from 'api';
import { StoreyDropdown } from 'components/storey-dropdown';

const SNav = styled.nav`
    display: flex;
    align-items: center;
    gap: ${({ theme }) => theme.padding.narrow};
    width: calc(100vw - 550px);
    overflow-x: auto;
    ${customScroll};
    flex: 4;
`;

const SArrowLeftIcon = styled(ArrowLeftIcon)`
    stroke: ${({ theme }) => theme.colors.revolver};
`;

const SWrapper = styled.ul`
    display: flex;
    align-items: center;
    padding: 0;
    list-style: none;
    margin: 0;
`;

const SBreadcrumbLi = styled.li`
    white-space: nowrap;
    color: ${({ theme }) => theme.colors.light_gray};

    &:not(:first-child):before {
        content: '/';
        display: inline-block;
        padding: 0 5px;
    }
`;

const SBreadcrumb = styled(Link)`
    color: ${({ theme }) => theme.colors.light_gray};
    text-decoration: none;
    padding: 5px 0;
    display: inline-block;
`;

const SActiveBreadcrumb = styled.span`
    display: inline-block;
    color: ${({ theme }) => theme.colors.dark_gray};
    padding: 5px 0;
`;

const SGoBackIcon = styled(IconButton)`
    display: flex;
    align-items: center;
    margin: 0;
`;

const SReferenceTag = styled.span`
    color: ${({ theme }) => theme.colors.purple_dark};
    font-size: ${({ theme }) => theme.font.size.medium};
`;

export type BreadcrumbItem = {
    name: string;
    path: string;
};

export const Breadcrumbs = () => {
    const {
        setReferenceFlag,
        setReferenceStructureId,
        state: { isReference, referenceStructureId },
    } = useStructureManagement();

    const { t } = useTranslation();
    const navigate = useNavigate();
    const { routes } = useValidatedRoutes();
    const [breadcrumbs, setBreadcrumbs] = useState<BreadcrumbItem[]>([{ path: '', name: '' }]);
    const [storeys, setStoreys] = useState<Structure[]>([]);
    const [currentStorey, setCurrentStorey] = useState<Structure | null>(null);

    const id = getStructureIdFromUrl(window.location.pathname);

    const { data: structureData } = useGetAncestors({ id: id || '' });
    const { data: currentStructureData } = useGetStructure({ id: id || '' });

    const currentStructureType = currentStructureData?.structure?.type;
    const ancestors = currentStructureData?.structure?.ancestors || [];

    const generalModeAncestorId =
        ancestors.find((ancestor) => ancestor.type === StructureType.GENERAL_MODE)?.id || null;

    let storeyStructure: Structure | null = null;
    let storeyParentId: string | null = null;

    if (currentStructureType === StructureType.STOREY) {
        storeyStructure = currentStructureData?.structure || null;
        storeyParentId = ancestors[0]?.id || null;
    } else {
        const storeyIndex = ancestors.findIndex(
            (ancestor) => ancestor.type === StructureType.STOREY,
        );
        if (storeyIndex !== -1) {
            storeyStructure = ancestors[storeyIndex] || null;
            storeyParentId = ancestors[storeyIndex - 1]?.id || null;
        }
    }

    const { data: parentData } = useGetStructureChildren({
        id: storeyParentId || '',
        perPage: 100,
    });

    const route = findMatchingRoute(routes, window.location.pathname) as RouteArgs;

    const organizationCrumb = useOrganizationDetailsCrumb();

    const location = useLocation();
    const searchParams = new URLSearchParams(location.search);
    const isReferenceParam = searchParams.get('isReference') === 'true';

    const isStructurePath = (path: string) => {
        const structuresRegex = /^\/structures\/[^/]+/;
        return structuresRegex.test(path);
    };

    useEffect(() => {
        if (isReference) {
            setStoreys([]);
            setCurrentStorey(null);
            return;
        }

        if (storeyStructure && parentData?.structure?.children?.nodes) {
            let storeysFound = parentData.structure.children.nodes.filter(
                (child: Structure) => child.type === StructureType.STOREY && !child.reference,
            ) as Structure[];

            const targetPolygonPath = (storeyStructure as any).svgPolygonPath;

            if (targetPolygonPath) {
                storeysFound = storeysFound.filter(
                    (child: Structure) => (child as any).svgPolygonPath === targetPolygonPath,
                );
            }

            if (storeysFound.length > 1) {
                setStoreys(storeysFound);

                const storeyId = storeyStructure.id;
                const current = storeysFound.find((s: Structure) => s.id === storeyId);
                setCurrentStorey(current || null);
            } else {
                setStoreys([]);
                setCurrentStorey(null);
            }
        } else {
            setStoreys([]);
            setCurrentStorey(null);
        }
    }, [storeyStructure, parentData, isReference, generalModeAncestorId]);

    useEffect(() => {
        if (isReferenceParam) {
            setReferenceFlag(true);
            if (id) {
                setReferenceStructureId(id);
            }
        }
    }, [isReferenceParam, id]);

    useEffect(() => {
        if (!isStructurePath(location.pathname) && isReference) {
            setReferenceFlag(false);
            setReferenceStructureId(null);
        }
    }, [location.pathname, isReference]);

    useEffect(() => {
        if (!route) {
            navigate(RoutePath.Structures, { replace: true });
        }
    }, [route]);

    useEffect(() => {
        if (structureData) {
            switch (route?.path) {
                case RoutePath.StructuresPreview: {
                    if (isReference) {
                        setBreadcrumbs(
                            generateReferenceBreadcrumb(structureData, referenceStructureId),
                        );
                    } else {
                        setBreadcrumbs(generateStructureCrumb(structureData));
                    }
                    break;
                }
                case RoutePath.StructuresEdit: {
                    const baseCrumbs = isReference
                        ? generateReferenceBreadcrumb(structureData, referenceStructureId)
                        : [
                              ...structureStart,
                              ...structureData.structure.ancestors.map(({ name, id, type }) =>
                                  singleStructureCrumb({ name, id, type }),
                              ),
                              singleStructureCrumb({
                                  name: structureData?.structure.name,
                                  id: structureData?.structure.id,
                                  type: structureData?.structure.type,
                              }),
                          ];

                    setBreadcrumbs([
                        ...baseCrumbs,
                        { name: t('bread-items.edit-structure'), path: '' },
                    ]);
                    break;
                }
                case RoutePath.StructuresManage: {
                    const baseCrumbs = isReference
                        ? generateReferenceBreadcrumb(structureData, referenceStructureId)
                        : generateStructureCrumb(structureData);

                    setBreadcrumbs([...baseCrumbs, { name: t('bread-items.manage'), path: '' }]);
                    break;
                }
                case RoutePath.StructuresFloorFinishingScheme: {
                    const baseCrumbs = isReference
                        ? generateReferenceBreadcrumb(structureData, referenceStructureId)
                        : generateStructureCrumb(structureData);

                    setBreadcrumbs([
                        ...baseCrumbs,
                        { name: t('bread-items.finishing-scheme'), path: '' },
                    ]);
                    break;
                }
                case RoutePath.StructuresCoordinationMode: {
                    const baseCrumbs = isReference
                        ? generateReferenceBreadcrumb(structureData, referenceStructureId)
                        : generateStructureCrumb(structureData);

                    setBreadcrumbs([
                        ...baseCrumbs,
                        { name: t('bread-items.coordination-mode'), path: '' },
                    ]);
                    break;
                }
                case RoutePath.StructuresDirectory: {
                    const baseCrumbs = isReference
                        ? generateReferenceBreadcrumb(structureData, referenceStructureId)
                        : generateStructureCrumb(structureData);

                    setBreadcrumbs([...baseCrumbs]);
                    break;
                }
            }
        } else {
            switch (route?.path) {
                case RoutePath.Structures: {
                    setBreadcrumbs(structuresList);
                    break;
                }
                case RoutePath.StructuresAdd: {
                    setBreadcrumbs(structureAdd);
                    break;
                }
                case RoutePath.Organizations: {
                    setBreadcrumbs(organizationsList);
                    break;
                }
                case RoutePath.OrganizationDetails: {
                    setBreadcrumbs([...organizationsStart, organizationCrumb as BreadcrumbItem]);
                    break;
                }
                case RoutePath.Users: {
                    setBreadcrumbs(usersList);
                    break;
                }
                case RoutePath.Settings: {
                    setBreadcrumbs(settings);
                    break;
                }
                case RoutePath.Profile: {
                    setBreadcrumbs(profile);
                    break;
                }
                case RoutePath.System3E: {
                    setBreadcrumbs(system3e);
                    break;
                }
            }
        }
    }, [route?.path, structureData, isReference, referenceStructureId, t]);

    const filteredBreadcrumbs = breadcrumbs.filter((item) => item?.name);

    return (
        <SNav aria-label="Breadcrumb">
            {filteredBreadcrumbs.length > 2 && (
                <SGoBackIcon
                    icon={<SArrowLeftIcon />}
                    title={t('bread-items.go-back')}
                    onClick={() => navigate(-1)}
                />
            )}
            {isReference && <SReferenceTag>{t('structure-preview.reference-tag')}</SReferenceTag>}
            <SWrapper>
                {filteredBreadcrumbs.map(({ path, name }, i: number) => {
                    const isLastItem = i + 1 === filteredBreadcrumbs.length;

                    const isStoreyBreadcrumb = storeyStructure && path.includes(storeyStructure.id);

                    const showStoreyDropdown =
                        isStoreyBreadcrumb && storeys.length > 1 && currentStorey;

                    return (
                        <SBreadcrumbLi key={`breadcrumb-${path}-${i}`}>
                            {showStoreyDropdown ? (
                                <StoreyDropdown storeys={storeys} currentStorey={currentStorey} />
                            ) : isLastItem ? (
                                <SActiveBreadcrumb aria-current="page">{t(name)}</SActiveBreadcrumb>
                            ) : (
                                <SBreadcrumb to={path}>{t(name)}</SBreadcrumb>
                            )}
                        </SBreadcrumbLi>
                    );
                })}
            </SWrapper>
        </SNav>
    );
};
